import SideBarTab from "./SideBarTab";
import { useState, useEffect } from "react";
import { usePortData } from "../contexts/PortDataContext";
import { Snackbar, Alert, useTheme } from "@mui/material";
import { getDeviceDetails } from "../containers/api/devices";

const SideBar = (props) => {
  const { deviceId } = props;

  const { selectedPort, setSelectedPort, setGlobalPortData } = usePortData();
  const [isConnected, setIsConnected] = useState(false);
  const [activeTab, setActiveTab] = useState("setup");
  const [reader, setReader] = useState(null);
  const [extractedData] = useState({
    ambientTemp: "0",
    objectTemp: "0",
    weight: "0",
    current: "0",
    rpm: "0",
    dutyCycle: "0",
  });

  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };


  useEffect(() => {
    const fetchDeviceDetails = async () => {
      try {
        setIsLoading(true);
        await getDeviceDetails(deviceId);
      } catch (error) {
        console.error("Error fetching device details:", error);
        setSnackbarMessage("Failed to load device settings");
        setOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeviceDetails();
  }, [deviceId]);



  // Function to handle port disconnection
  const handleDisconnect = async () => {
    try {
      if (reader) {
        await reader.cancel();
        await reader.releaseLock();
        setReader(null);
      }

      if (selectedPort) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        await selectedPort.close();
      }

      setIsConnected(false);
      // setGlobalPortData("");
      setSnackbarMessage("Disconnected from port.");
      setOpen(true);
    } catch (err) {
      console.error("Error during serial port disconnection:", err);
      setIsConnected(false);
      setSnackbarMessage("Error disconnecting from port.");
      setOpen(true);
    }
  };

  const handleConnect = async () => {
    try {
      const port = selectedPort;
      if (port) {
        await port.open({ baudRate: 115200 });

        setIsConnected(true);

        const textDecoder = new TextDecoderStream("utf-8");
        port.readable.pipeTo(textDecoder.writable);
        const reader = textDecoder.readable.getReader();
        setReader(reader);

        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          if (value) {
            buffer += value;

            if (buffer.includes("\n")) {
              const lines = buffer.split("\n");
              buffer = lines.pop();

              lines.forEach((line) => {
                const cleanedData = line.replace(/[^\x20-\x7E]/g, "");
                setGlobalPortData(cleanedData);
              });
            }
          }
        }
      }
    } catch (err) {
      console.error("Error while connecting to serial port:", err);
      setIsConnected(false); // Ensure isConnected is false on error
    }
  };


  // Function to request user-selected serial port
  const requestPort = async () => {
    try {
      if (isConnected && selectedPort) {
        await handleDisconnect();
      }

      const port = await navigator?.serial?.requestPort();
      setSelectedPort(port);

      setTimeout(() => {
        handleConnect();
      }, 100);

      setSnackbarMessage("Port selected successfully! Connecting...");
      setOpen(true);
    } catch (err) {
      console.error("No port selected or error requesting port:", err);
      setSelectedPort(null);
      setSnackbarMessage("Error selecting port. Please try again.");
      setOpen(true);
    }
  };

  return (
    <div
      className="thrust-device-controls flex rounded-md box-border flex-col justify-between"
      style={{
        backgroundColor: theme.palette.primary.main,
        border: `1px solid ${theme.palette.border.color}`,
      }}
    >
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbarMessage.toLowerCase().includes("error") ? "error" : "success"}
          sx={{ width: "100%", color: "white" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <div className="flex flex-col flex-shrink-0 w-72">
        <div className="flex-1 p-4">
          <div className="pt-4 flex flex-col gap-2">
            <SideBarTab
              label="Setup"
              link="setup"
              bgColor="red"
              onClick={() => setActiveTab("setup")}
              isActive={activeTab === "setup"}
            />
            <SideBarTab
              label="Manual Control"
              link="manual-control"
              bgColor={activeTab === "/manual-control" ? "bg-[#333333]" : ""}
              onClick={() => setActiveTab("/manual-control")}
              isActive={activeTab === "/manual-control"}
            />
            <SideBarTab
              label="Export"
              link="export"
              bgColor={activeTab === "/export" ? "bg-[#333333]" : ""}
              onClick={() => setActiveTab("/export")}
              isActive={activeTab === "/export"}
            />
          </div>

          {/* Port Section */}
          <div className="px-2 py-2 mt-4">
            <h4 className="text-[#EEEEEE] font-semibold text-sm">Port</h4>
            <button
              onClick={requestPort}
              className="mt-2 py-1 w-full text-sm bg-[#306AF6] text-white rounded"
              disabled={isLoading}
            >
              Click to Select Port
            </button>
          </div>

          {/* Connect/Disconnect Button */}
          <div className="px-2 py-2">
            <button
              onClick={isConnected ? handleDisconnect : handleConnect}
              className={`w-full py-1 text-sm text-[#EEEEEE] rounded duration-300 ${
                isConnected
                  ? "bg-red-600 hover:bg-[#800000]"
                  : "bg-[#306AF6] hover:bg-[#1F316F]"
              }`}
              disabled={isLoading}
            >
              {isConnected ? "Disconnect" : "Connect"}
            </button>
          </div>

          {/* Display Data from Port */}
          {isConnected && (
            <div className="px-2 py-4 flex flex-col gap-2">
              <div className="flex flex-col">
                <p className="text-white text-base font-semibold">Hardware</p>
                <p className="text-gray-400 text-xs font-light">Connected!</p>
              </div>

              <div className="flex flex-col">
                <p className="text-white text-base font-semibold">Software</p>
                <p className="text-gray-400 text-xs font-light">
                  Ambient Temperature:{" "}
                  <span className="text-white">{extractedData.ambientTemp} °C</span>
                </p>
                <p className="text-gray-400 text-xs font-light">
                  Object Temperature:{" "}
                  <span className="text-white">{extractedData.objectTemp} °C</span>
                </p>
                <p className="text-gray-400 text-xs font-light">
                  Weight: <span className="text-white">{extractedData.weight} g</span>
                </p>
                <p className="text-gray-400 text-xs font-light">
                  Current: <span className="text-white">{extractedData.current} A</span>
                </p>
                <p className="text-gray-400 text-xs font-light">
                  RPM: <span className="text-white">{extractedData.rpm} rpm</span>
                </p>
                <p className="text-gray-400 text-xs font-light">
                  Duty Cycle:{" "}
                  <span className="text-white">{extractedData.dutyCycle} %</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideBar;
